import { useRef, useState } from "react";
import { Camera, Image as ImageIcon, X, Check, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { isNative, takeNativePhoto, dataUrlToFile } from "@/lib/native";
import { toast } from "sonner";
import {
  Popover, PopoverTrigger, PopoverContent,
} from "@/components/ui/popover";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";

/**
 * Universal photo picker giving users BOTH options:
 *   1. Take photo  (device camera, optional multi-capture)
 *   2. Upload from gallery (file picker, optional multi-select)
 *
 * Props
 *  - onSelect(files: File[])   called with the chosen files
 *  - multiple                  default true — when false returns exactly one file
 *  - accept                    default "image/jpeg,image/png,image/webp"
 *  - cameraFacing              "user" | "environment"  default "environment"
 *  - children                  the trigger element (rendered as PopoverTrigger)
 *  - testId                    base data-testid prefix
 *  - disabled                  disables the popover trigger
 */
export default function PhotoPicker({
  onSelect,
  multiple = true,
  accept = "image/jpeg,image/png,image/webp",
  cameraFacing = "environment",
  children,
  testId = "photo-picker",
  disabled = false,
}) {
  const cameraRef = useRef();
  const galleryRef = useRef();
  const [open, setOpen] = useState(false);
  const [captured, setCaptured] = useState([]);
  const [reviewOpen, setReviewOpen] = useState(false);

  const closeMenu = () => setOpen(false);

  /**
   * Native-aware camera trigger.
   *  - Native (Capacitor): opens the OS camera via Camera.getPhoto, converts
   *    the returned data-URL into a File, and pipes it through the same
   *    onCamera flow used by the web <input capture>.
   *  - Web: clicks the hidden <input> that has capture=<facing>.
   * If the native call throws (e.g. permission denied) we fall back to the
   * hidden input so the user is never stuck.
   */
  const triggerCamera = async () => {
    if (isNative()) {
      try {
        const dataUrl = await takeNativePhoto();
        if (!dataUrl) return; // user cancelled
        const file = dataUrlToFile(dataUrl);
        if (!file) throw new Error("bad-photo");
        if (!multiple) {
          closeMenu();
          onSelect([file]);
          return;
        }
        setCaptured((prev) => [...prev, file]);
        setReviewOpen(true);
        closeMenu();
        return;
      } catch (e) {
        // Permission denied / hardware error → fall back to web input.
        if (String(e?.message || "").toLowerCase().includes("denied")) {
          toast.error("Camera permission denied.");
          return;
        }
        console.warn("Native camera failed, falling back to web input", e);
      }
    }
    cameraRef.current?.click();
  };

  const onGallery = (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    closeMenu();
    onSelect(multiple ? files : [files[0]]);
  };

  const onCamera = (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    if (!multiple) {
      closeMenu();
      onSelect([files[0]]);
      return;
    }
    setCaptured((prev) => [...prev, ...files]);
    setReviewOpen(true);
    closeMenu();
  };

  const confirmMulti = () => {
    if (captured.length === 0) return;
    const files = captured;
    setCaptured([]);
    setReviewOpen(false);
    onSelect(files);
  };

  const cancelMulti = () => {
    setCaptured([]);
    setReviewOpen(false);
  };

  const removeAt = (idx) =>
    setCaptured((arr) => arr.filter((_, i) => i !== idx));

  return (
    <>
      <Popover open={open} onOpenChange={(o) => !disabled && setOpen(o)}>
        <PopoverTrigger asChild disabled={disabled}>
          {children}
        </PopoverTrigger>
        <PopoverContent
          className="rounded-none w-64 p-0 overflow-hidden"
          align="end"
          data-testid={`${testId}-menu`}
        >
          <button
            type="button"
            data-testid={`${testId}-camera-btn`}
            onClick={triggerCamera}
            className="w-full flex items-center gap-3 px-4 py-3 hover:bg-muted text-left border-b border-border"
          >
            <Camera size={16} />
            <div>
              <p className="text-sm">Take photo</p>
              <p className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">
                {multiple ? "Capture multiple" : "One shot"}
              </p>
            </div>
          </button>
          <button
            type="button"
            data-testid={`${testId}-gallery-btn`}
            onClick={() => galleryRef.current?.click()}
            className="w-full flex items-center gap-3 px-4 py-3 hover:bg-muted text-left"
          >
            <ImageIcon size={16} />
            <div>
              <p className="text-sm">Upload from gallery</p>
              <p className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">
                {multiple ? "Select multiple" : "Choose one"}
              </p>
            </div>
          </button>
        </PopoverContent>
      </Popover>

      <input
        ref={cameraRef}
        type="file"
        accept={accept}
        capture={cameraFacing}
        className="hidden"
        onChange={onCamera}
        data-testid={`${testId}-camera-input`}
      />
      <input
        ref={galleryRef}
        type="file"
        accept={accept}
        multiple={multiple}
        className="hidden"
        onChange={onGallery}
        data-testid={`${testId}-gallery-input`}
      />

      <Dialog
        open={reviewOpen}
        onOpenChange={(o) => { if (!o) cancelMulti(); }}
      >
        <DialogContent
          className="rounded-none max-w-md"
          data-testid={`${testId}-review-dialog`}
        >
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">
              Captured · {captured.length} photo{captured.length === 1 ? "" : "s"}
            </DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-3 gap-2 max-h-72 overflow-y-auto">
            {captured.map((f, i) => (
              <div key={`${f.name}-${i}`} className="relative">
                <img
                  src={URL.createObjectURL(f)}
                  alt={`shot ${i + 1}`}
                  className="w-full aspect-square object-cover border border-border"
                />
                <button
                  type="button"
                  onClick={() => removeAt(i)}
                  className="absolute top-1 right-1 bg-accent text-accent-foreground rounded-full p-1"
                  aria-label="Remove photo"
                  data-testid={`${testId}-remove-${i}`}
                >
                  <X size={10} />
                </button>
              </div>
            ))}
          </div>
          <DialogFooter className="flex-row gap-2 sm:gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={triggerCamera}
              className="rounded-none uppercase tracking-[0.18em] text-xs flex-1"
              data-testid={`${testId}-add-more`}
            >
              <Plus size={14} className="mr-2" /> Take another
            </Button>
            <Button
              type="button"
              onClick={confirmMulti}
              disabled={captured.length === 0}
              className="rounded-none uppercase tracking-[0.18em] text-xs flex-1"
              data-testid={`${testId}-confirm`}
            >
              <Check size={14} className="mr-2" />
              Use {captured.length || ""}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
